/*
 * Lotus global add-to-cart handler.
 *
 * Intercepts every <form> POST to /cart/add anywhere in the store and routes the
 * response through <cart-drawer>.renderContents() so the cart DRAWER opens
 * instead of the cart PAGE — without requiring each form to be wrapped in
 * <product-form> or carry Dawn's specific span/spinner markup.
 *
 * If the cart type is set to "page" in theme settings, this script is not
 * loaded (gated in theme.liquid), so behavior falls back to native form POST.
 */
(function () {
  'use strict';

  function getCheckoutUrl() {
    // If a section has stashed a discount code on the page, route the customer
    // through Shopify's /discount/CODE?redirect=/checkout so the discount cookie
    // is set + applied on the way to the checkout page.
    var code = (typeof window.LOTUS_DISCOUNT_CODE === 'string' && window.LOTUS_DISCOUNT_CODE) || '';
    if (!code) return '/checkout';
    return '/discount/' + encodeURIComponent(code) + '?redirect=/checkout';
  }

  function getCartAddUrl() {
    return (window.routes && window.routes.cart_add_url) || '/cart/add';
  }

  function isCartAddForm(form) {
    if (!form || form.tagName !== 'FORM') return false;
    // Skip forms already handled by Dawn's <product-form> — its onSubmitHandler
    // calls preventDefault() and dispatches its own AJAX path.
    if (form.closest('product-form')) return false;
    var action = form.getAttribute('action') || '';
    if (!action) return false;
    // Match /cart/add and any localized prefix (e.g. /en-au/cart/add).
    return /\/cart\/add(\.js|\?|$)/.test(action) || action.indexOf(getCartAddUrl()) !== -1;
  }

  function showError(message) {
    // Soft-fail: log + alert. Dawn's per-form error wrappers aren't guaranteed
    // to exist on every card, so a minimal fallback is fine.
    console.error('[lotus add-to-cart]', message);
    if (message) {
      try {
        window.alert(message);
      } catch (e) { /* no-op */ }
    }
  }

  // Intercept the cart drawer's own 'Checkout' button so it routes through
  // /discount/CODE when a discount is stashed on the page. This catches the
  // standard drawer-checkout flow (after add-to-cart-drawer-open).
  document.addEventListener('click', function (event) {
    var btn = event.target.closest && event.target.closest('.cart-drawer-lotus__checkout');
    if (!btn) return;
    var url = getCheckoutUrl();
    if (url === '/checkout') return; // No discount code stashed; let Shopify handle it natively
    event.preventDefault();
    window.location.href = url;
  }, true);

  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!isCartAddForm(form)) return;

      // The triggering button can opt this submission out of the drawer flow
      // and route straight to checkout instead. Read from event.submitter (the
      // actual button clicked) so multiple buy buttons in one form work.
      var submitterBtn = event.submitter || form.querySelector('[type="submit"]');
      var goToCheckout = !!(submitterBtn && submitterBtn.getAttribute('data-fp-checkout') === 'true');

      // ===== Fast path: 'Go straight to checkout' =====
      // Real AJAX add to /cart/add.js (NOT the cart permalink). The permalink
      // form /cart/{id}:{qty} is fragile after a bfcache Back from /checkout:
      // the browser treats the identical URL as an already-completed redirect
      // and the cart-add doesn't re-execute, so a second click intended to be
      // the 2nd add silently does nothing. AJAX add to /cart/add.js always
      // accumulates and survives bfcache.
      if (goToCheckout) {
        event.preventDefault();
        var fd = new FormData(form);
        var variantId = fd.get('id');
        if (variantId) {
          if (submitterBtn) {
            submitterBtn.setAttribute('aria-disabled', 'true');
            submitterBtn.classList.add('loading', 'is-adding');
          }
          fetch(getCartAddUrl(), {
            method: 'POST',
            headers: {
              'Accept': 'application/javascript',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: fd,
            cache: 'no-store',
            credentials: 'same-origin'
          })
            .catch(function () { /* best-effort — still navigate to checkout */ })
            .finally(function () {
              var code = (typeof window.LOTUS_DISCOUNT_CODE === 'string' && window.LOTUS_DISCOUNT_CODE) || '';
              var dest = code
                ? '/discount/' + encodeURIComponent(code) + '?redirect=/checkout'
                : '/checkout';
              window.location.href = dest;
            });
          return;
        }
      }

      var cartDrawer = document.querySelector('cart-drawer');
      // No drawer on the page (cart_type === 'page') — let the native POST run.
      if (!cartDrawer) return;

      event.preventDefault();

      var submitButton = submitterBtn || form.querySelector('[type="submit"]');
      var originalButtonHTML = null;
      var addingLabel = (submitButton && submitButton.getAttribute('data-adding-text')) || 'Adding';
      if (submitButton) {
        if (submitButton.getAttribute('aria-disabled') === 'true') return;
        submitButton.setAttribute('aria-disabled', 'true');
        submitButton.classList.add('loading', 'is-adding');
        originalButtonHTML = submitButton.innerHTML;
        // Stash on the element too — survives bfcache restore so the pageshow
        // handler at the bottom of this file can recover stuck "Adding…" state.
        submitButton.setAttribute('data-original-html', originalButtonHTML);
        submitButton.innerHTML =
          '<span class="lotus-add-spinner" aria-hidden="true"></span>' +
          '<span class="lotus-add-label">' + addingLabel + '</span>';
      }

      var formData = new FormData(form);
      if (cartDrawer && !goToCheckout) {
        try {
          var sections = cartDrawer.getSectionsToRender().map(function (s) { return s.id; });
          formData.append('sections', sections.join(','));
          formData.append('sections_url', window.location.pathname);
        } catch (e) { /* getSectionsToRender unavailable; continue without */ }
        cartDrawer.setActiveElement && cartDrawer.setActiveElement(document.activeElement);
      }

      var config = {
        method: 'POST',
        headers: {
          'Accept': 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      };

      fetch(getCartAddUrl(), config)
        .then(function (r) { return r.json(); })
        .then(function (response) {
          if (response.status) {
            showError(response.description || response.message || 'Could not add to cart');
            return;
          }
          // Notify any subscribers (cart-icon-bubble, etc.)
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              cartData: response
            });
          }
          if (goToCheckout) {
            // Skip the drawer entirely and route through /discount/CODE if a
            // section has stashed one — Shopify applies the discount on the
            // way to checkout.
            window.location.href = getCheckoutUrl();
            return;
          }
          if (cartDrawer) cartDrawer.renderContents(response);
        })
        .catch(function (err) {
          showError(err && err.message);
        })
        .finally(function () {
          // Don't restore the button HTML if we're navigating away — avoids a
          // brief flicker back to the original price as the redirect kicks in.
          if (submitButton && !goToCheckout) {
            submitButton.removeAttribute('aria-disabled');
            submitButton.classList.remove('loading', 'is-adding');
            submitButton.removeAttribute('data-original-html');
            if (originalButtonHTML !== null) submitButton.innerHTML = originalButtonHTML;
          }
        });
    },
    false
  );

  // ============================================================
  // Cart drawer overlay — document-level delegated close listener
  // ------------------------------------------------------------
  // Dawn binds the overlay's close listener inside connectedCallback,
  // then re-binds inside renderContents (a setTimeout-driven step).
  // After an add-to-cart, #CartDrawer.innerHTML is swapped and the
  // re-bind is order-fragile — the freshly inserted overlay sometimes
  // ends up with no click handler, so clicking outside the drawer
  // does nothing once the cart has items. A document-level delegated
  // listener survives any innerHTML swap because it's anchored to
  // document, not the overlay element.
  // ============================================================
  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;
    var overlay = target.closest('#CartDrawer-Overlay, .cart-drawer-lotus__overlay');
    if (!overlay) return;
    var drawer = overlay.closest('cart-drawer');
    if (drawer && typeof drawer.close === 'function') {
      drawer.close();
    }
  });

  // ============================================================
  // Hero primary CTA -> featured-product checkout button
  // ------------------------------------------------------------
  // The hero's primary CTA should fire the exact same flow as the
  // featured-product section's "Add to cart" button (same product,
  // same discount, AJAX add + redirect to /discount/CODE?redirect=
  // /checkout). Rather than duplicate that wiring on the hero, we
  // find the featured-product checkout button on the page and
  // .click() it programmatically — auto-matches whatever product
  // the featured section is currently configured with.
  //
  // Resolution order on click:
  //   1) document.querySelector('.featured-product-lotus [data-fp-checkout]')
  //      → preventDefault + .click() (its form-submit handler above
  //      runs the AJAX-add → redirect to checkout flow).
  //   2) If no featured section is on the page AND the hero has its
  //      own data-lw-checkout-variant (filled by the optional fallback
  //      schema settings), run the AJAX-add + redirect inline.
  //   3) Otherwise let the <a href> navigate normally (no-JS path).
  // ============================================================
  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;
    var heroBtn = target.closest('[data-lw-hero-primary-cta]');
    if (!heroBtn) return;

    // (1) Featured-product checkout button on this page
    var featuredBtn = document.querySelector(
      '.featured-product-lotus [data-fp-checkout="true"], ' +
      '.featured-product-lotus [data-fp-checkout]'
    );
    if (featuredBtn) {
      event.preventDefault();
      featuredBtn.click();
      return;
    }

    // (2) Hero's own product fallback (optional schema settings)
    var vid = heroBtn.getAttribute('data-lw-checkout-variant');
    if (vid) {
      event.preventDefault();
      heroBtn.setAttribute('aria-disabled', 'true');
      heroBtn.classList.add('is-adding');
      var qty = parseInt(heroBtn.getAttribute('data-lw-checkout-qty') || '1', 10) || 1;
      var code = (heroBtn.getAttribute('data-lw-checkout-discount') || '').trim();
      var formData = new FormData();
      formData.append('id', vid);
      formData.append('quantity', String(qty));
      formData.append('form_type', 'product');
      fetch(getCartAddUrl(), {
        method: 'POST',
        headers: {
          'Accept': 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData,
        cache: 'no-store',
        credentials: 'same-origin'
      })
        .catch(function () { /* best-effort */ })
        .finally(function () {
          var dest = code
            ? '/discount/' + encodeURIComponent(code) + '?redirect=/checkout'
            : '/checkout';
          window.location.href = dest;
        });
      return;
    }

    // (3) No interception — let <a href> navigate.
  });

  // ============================================================
  // bfcache restore (Back button from /checkout)
  // ------------------------------------------------------------
  // When a shopper proceeds to /checkout and then hits the browser's
  // Back button, modern browsers restore the previous page from the
  // back/forward cache. That restore freezes the DOM exactly as it was
  // mid-submit: the Add-to-cart button is stuck on "Adding…" and the
  // cart drawer body still reflects whatever was visible before the
  // navigation (often the empty state, even though the cart now has
  // items). Neither auto-resolves — we get the persisted=true pageshow
  // event but no script gets to undo the loading state.
  //
  // The fix: listen for pageshow with event.persisted === true and
  //   1) restore every button we previously swapped to a spinner, AND
  //   2) refetch the cart drawer + cart-icon-bubble HTML via Shopify's
  //      Section Rendering API and swap it in place (without opening
  //      the drawer). Cart counts + line items resync instantly.
  // ============================================================
  function lotusRestoreButtons() {
    // Buttons whose innerHTML was swapped to a spinner — recover from
    // the data-original-html attribute we stash at submit time. Survives
    // bfcache because it lives on the DOM, not in a JS variable.
    var stashed = document.querySelectorAll('button[data-original-html]');
    Array.prototype.forEach.call(stashed, function (btn) {
      try { btn.innerHTML = btn.getAttribute('data-original-html'); } catch (e) { /* noop */ }
      btn.removeAttribute('data-original-html');
      btn.removeAttribute('aria-disabled');
      btn.classList.remove('loading', 'is-adding');
    });
    // Fast-checkout path only flips classes/aria (innerHTML unchanged),
    // so just clear those — no HTML restore needed.
    var classOnly = document.querySelectorAll('button.is-adding, button.loading');
    Array.prototype.forEach.call(classOnly, function (btn) {
      btn.removeAttribute('aria-disabled');
      btn.classList.remove('loading', 'is-adding');
    });
  }

  function lotusRefreshCartSections() {
    var path = window.location.pathname || '/';
    try {
      fetch(path + '?sections=cart-drawer,cart-icon-bubble', {
        credentials: 'same-origin',
        cache: 'no-store'
      })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (data) {
          if (!data) return;
          // Cart drawer host: replace inner HTML so the <cart-drawer>
          // web component instance survives (event listeners stay bound).
          if (data['cart-drawer']) {
            var drawerEl = document.querySelector('cart-drawer');
            if (drawerEl) {
              var tmp = document.createElement('div');
              tmp.innerHTML = data['cart-drawer'];
              var newRoot = tmp.querySelector('cart-drawer');
              if (newRoot) {
                drawerEl.innerHTML = newRoot.innerHTML;
                // Re-sync the is-empty modifier on the host so the
                // empty-state styling matches the fresh server-side count.
                if (newRoot.classList.contains('is-empty')) drawerEl.classList.add('is-empty');
                else drawerEl.classList.remove('is-empty');
              }
            }
          }
          // Cart icon bubble (header count badge)
          if (data['cart-icon-bubble']) {
            var bubble = document.querySelector('#cart-icon-bubble');
            if (bubble) {
              var tmp2 = document.createElement('div');
              tmp2.innerHTML = data['cart-icon-bubble'];
              var newBubble = tmp2.querySelector('#cart-icon-bubble');
              if (newBubble) bubble.innerHTML = newBubble.innerHTML;
            }
          }
        })
        .catch(function () { /* network error — leave as-is */ });
    } catch (e) { /* noop */ }
  }

  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return; // only act on bfcache restore
    lotusRestoreButtons();
    lotusRefreshCartSections();
  });
})();
